import { CMD } from './commands';
/**
 * Thin WebSocket wrapper that mirrors the connect/reconnect behavior of
 * `startSync()` in `web/html/js/player_utils.js`:
 *
 *   1. GET `ajax/sync.php` to obtain a short-lived token (SHA1 of user id +
 *      random + timestamp). Session users get the session token instead.
 *   2. Open `wss://<host>/remote/{token}`.
 *   3. Reply `KPONG` to each server `KPING`.
 *   4. On close/error, retry after 20 seconds.
 */
const RECONNECT_DELAY_MS = 20_000;
/**
 * Resolve the WebSocket base URL, matching the legacy `player_utils.js`
 * behavior. PHP injects a `socketServerAddress()` global (see
 * `web/html/include/PageTemplate.php` → `websocket_url()`) that already
 * encodes:
 *
 *   - protocol: `wss://` for HTTPS pages, otherwise `ws://`
 *   - domain:   the current request host
 *   - port:     `:8000` (or whatever `$_websocket_port` is) on
 *               self-hosted deployments where `$_websocket_use_web_ports`
 *               is false. Web-port deployments (cloudflare, ALB) omit it.
 *
 * We must use that value when present — deriving from `window.location` alone
 * gets the port wrong on self-hosted servers.
 */
export function deriveSocketBaseUrl() {
    if (typeof window === 'undefined')
        return '';
    const fn = window.socketServerAddress;
    if (typeof fn === 'function') {
        try {
            const url = fn();
            if (typeof url === 'string' && url.length > 0)
                return url;
        }
        catch {
            // fall through
        }
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
}
/**
 * Client that exposes the {@link SyncedSessionTransport} surface the manager
 * talks to. `start()` kicks off the token fetch + WS open; `stop()` tears
 * down and cancels any pending reconnect.
 */
export class SyncedSessionClient {
    options;
    ws = null;
    reconnectTimer = null;
    stopped = false;
    messageHandler = null;
    openHandler = null;
    closeHandler = null;
    constructor(opts = {}) {
        this.options = {
            fetchImpl: opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : (() => { throw new Error('fetch unavailable'); })),
            WebSocketImpl: opts.WebSocketImpl ?? (typeof WebSocket !== 'undefined' ? WebSocket : null),
            socketBaseUrl: opts.socketBaseUrl ?? deriveSocketBaseUrl(),
            tokenUrl: opts.tokenUrl ?? 'ajax/sync.php',
            autoReconnect: opts.autoReconnect ?? true,
            // Bind to globalThis so the browser's setTimeout/clearTimeout receive
            // the right `this` when invoked through the options object.
            setTimeoutImpl: opts.setTimeoutImpl ?? setTimeout.bind(globalThis),
            clearTimeoutImpl: opts.clearTimeoutImpl ?? clearTimeout.bind(globalThis),
        };
    }
    start() {
        this.stopped = false;
        void this.openConnection();
    }
    stop() {
        this.stopped = true;
        if (this.reconnectTimer !== null) {
            this.options.clearTimeoutImpl(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        try {
            this.ws?.close();
        }
        catch {
            // ignore
        }
        this.ws = null;
    }
    send(message) {
        const ws = this.ws;
        if (!ws || ws.readyState !== 1 /* OPEN */) {
            // Log dropped sends so the user can spot timing bugs (e.g. KREADY
            // fired before the WS was open).
            // eslint-disable-next-line no-console
            console.warn('[synced-session] ⨯ dropped (socket not open):', message);
            return;
        }
        // eslint-disable-next-line no-console
        console.log('[synced-session] →', message);
        try {
            ws.send(message);
        }
        catch {
            // ignore: connection may have dropped between the readyState check and send
        }
    }
    close() { this.stop(); }
    onMessage(cb) { this.messageHandler = cb; }
    onOpen(cb) { this.openHandler = cb; }
    onClose(cb) { this.closeHandler = cb; }
    async openConnection() {
        // `ajax/sync.php` returns JSON: `{ token, url }` on success or
        // `{ error }` on failure. The `url` is the canonical WebSocket base
        // URL from PHP's `websocket_url()` and supersedes any base URL the
        // client was constructed with — it's the only source the server
        // guarantees is correct (cloud uses a separate domain, self-hosted
        // may use a non-web port).
        let token;
        let baseUrl = this.options.socketBaseUrl;
        try {
            const response = await this.options.fetchImpl(this.options.tokenUrl);
            const payload = await response.json();
            if (typeof payload.error === 'string' || typeof payload.token !== 'string') {
                if (!this.stopped)
                    this.scheduleReconnect();
                return;
            }
            token = payload.token.trim();
            if (typeof payload.url === 'string' && payload.url.length > 0) {
                baseUrl = payload.url;
            }
        }
        catch {
            this.scheduleReconnect();
            return;
        }
        if (!token || this.stopped) {
            if (!this.stopped)
                this.scheduleReconnect();
            return;
        }
        const Ctor = this.options.WebSocketImpl;
        if (!Ctor) {
            this.scheduleReconnect();
            return;
        }
        const url = `${baseUrl}/remote/${encodeURIComponent(token)}`;
        let ws;
        try {
            ws = new Ctor(url);
        }
        catch {
            this.scheduleReconnect();
            return;
        }
        this.ws = ws;
        ws.onopen = () => { this.openHandler?.(); };
        ws.onclose = () => {
            if (this.ws === ws)
                this.ws = null;
            this.closeHandler?.();
            this.scheduleReconnect();
        };
        ws.onerror = () => {
            // onclose will follow — let its handler do the reconnect to avoid
            // double-scheduling.
        };
        ws.onmessage = (event) => {
            const data = typeof event.data === 'string' ? event.data : '';
            if (!data)
                return;
            // Log every incoming command for debugging (matches the legacy
            // player_utils.js `console.log('data: ' + message.data)` line).
            // Excludes our own KPONG replies — only inbound traffic is logged.
            // eslint-disable-next-line no-console
            console.log('[synced-session] ←', data);
            if (data === CMD.PING) {
                this.send(CMD.PONG);
                return;
            }
            this.messageHandler?.(data);
        };
    }
    scheduleReconnect() {
        if (this.stopped || !this.options.autoReconnect)
            return;
        if (this.reconnectTimer !== null)
            return;
        this.reconnectTimer = this.options.setTimeoutImpl(() => {
            this.reconnectTimer = null;
            if (this.stopped)
                return;
            void this.openConnection();
        }, RECONNECT_DELAY_MS);
    }
    /**
     * Adapter to the {@link SyncedSessionTransport} interface used by the
     * manager. Keeping this separate lets tests supply a fake transport
     * without involving fetch/WebSocket at all.
     */
    asTransport() {
        return {
            send: (msg) => this.send(msg),
            close: () => this.close(),
            onMessage: (cb) => this.onMessage(cb),
            onOpen: (cb) => this.onOpen(cb),
            onClose: (cb) => this.onClose(cb),
        };
    }
}
export { RECONNECT_DELAY_MS };
